/**
 * Chip Component
 *
 * A flexible chip component with optional left icon, text, and right icon.
 * Supports two types: "tag" and "badge".
 *
 * Badge supports two styles:
 * - "outline" (Text colored): transparent background with colored border and text
 * - "filled" (Badge colored): colored background with matching border and text
 *
 * Badge color variants: default, brand, success, warning, error, info
 *
 * @example
 * // Tag variant (default)
 * <Chip type="tag" text="Label" />
 *
 * @example
 * // Badge outline with brand color
 * <Chip type="badge" variant="outline" colorScheme="brand" text="Status" />
 *
 * @example
 * // Badge filled with success color
 * <Chip type="badge" variant="filled" colorScheme="success" text="Active" />
 *
 * @example
 * // With custom text and background color overrides
 * <Chip type="badge" text="Custom" textColorClass="text-text-error" bgColorClass="bg-error" />
 *
 * @example
 * // With icons
 * import TagIcon from 'src/assets/tag.svg';
 * import CloseIcon from 'src/assets/close.svg';
 * <Chip type="tag" text="Removable" leftIcon={TagIcon} rightIcon={CloseIcon} onRightIconClick={handleRemove} />
 */
import { cva, type VariantProps } from 'class-variance-authority';
import * as React from 'react';
import { cn } from 'src/utils/utils';
import { Icon, type IconProps } from './icon';
import { Typography } from './typography';

const chipVariants = cva(
  `
    tw:inline-flex
    tw:items-center
    tw:gap-2xs

    tw:w-max

    tw:overflow-hidden

    tw:py-2xs
    tw:px-s
  `,
  {
    variants: {
      type: {
        tag: 'tw:border tw:border-solid tw:border-border-primary tw:bg-secondary tw:rounded-full tw:justify-center tw:text-text-primary tw:hover:bg-secondary-hover',
        badge: 'tw:rounded-full tw:border tw:border-solid'
      },
      variant: {
        outline: 'tw:bg-primary tw:border-border-secondary',
        filled: 'tw:border-none'
      },
      colorScheme: {
        default: '',
        brand: '',
        success: '',
        warning: '',
        error: '',
        info: ''
      },
      size: {
        medium: '',
        large: '',
        small: ''
      },
      interactive: {
        true: '',
        false: ''
      },
      disabled: {
        true: 'tw:bg-btn-bg-disabled tw:border-btn-border-disabled tw:cursor-not-allowed',
        false: ''
      }
    },
    compoundVariants: [
      {
        type: 'tag',
        interactive: true,
        disabled: false,
        className:
          'tw:cursor-pointer tw:transition-colors tw:duration-200 tw:ease-in-out'
      },
      {
        type: 'tag',
        colorScheme: 'default',
        interactive: true,
        disabled: false,
        className:
          'tw:bg-secondary tw:border-border-primary tw:hover:bg-secondary-hover'
      },
      {
        type: 'tag',
        colorScheme: 'error',
        disabled: false,
        className: 'tw:bg-error tw:border-border-error'
      },
      {
        type: 'tag',
        colorScheme: 'error',
        interactive: true,
        disabled: false,
        className: 'tw:hover:bg-error-hover tw:hover:border-border-error-hover'
      },

      // ============================================
      // BADGE FILLED VARIANTS (Badge colored)
      // Colored background with matching border
      // ============================================
      // Filled - Default (Grey)
      {
        type: 'badge',
        variant: 'filled',
        colorScheme: 'default',
        className: 'tw:bg-secondary'
      },
      // Filled - Brand
      {
        type: 'badge',
        variant: 'filled',
        colorScheme: 'brand',
        className: 'tw:bg-brand-light'
      },
      // Filled - Success
      {
        type: 'badge',
        variant: 'filled',
        colorScheme: 'success',
        className: 'tw:bg-success'
      },
      // Filled - Warning
      {
        type: 'badge',
        variant: 'filled',
        colorScheme: 'warning',
        className: 'tw:bg-warning'
      },
      // Filled - Error
      {
        type: 'badge',
        variant: 'filled',
        colorScheme: 'error',
        className: 'tw:bg-error'
      },
      // Filled - Info
      {
        type: 'badge',
        variant: 'filled',
        colorScheme: 'info',
        className: 'tw:bg-info'
      }
    ],
    defaultVariants: {
      type: 'tag',
      variant: 'outline',
      colorScheme: 'default',
      size: 'medium',
      interactive: false,
      disabled: false
    }
  }
);

/**
 * Get text color class based on badge colorScheme variant
 */
const getTextColorClass = (
  type: 'tag' | 'badge' | null | undefined,
  colorScheme:
    | 'default'
    | 'brand'
    | 'success'
    | 'warning'
    | 'error'
    | 'info'
    | null
    | undefined,
  interactive = false,
  disabled = false
): string => {
  if (type === 'tag') {
    if (disabled) {
      return 'tw:text-btn-fg-disabled';
    }
    if (colorScheme === 'error') {
      return interactive
        ? 'tw:text-text-error tw:group-hover:text-text-error-hover'
        : 'tw:text-text-error';
    }
    return 'tw:text-text-primary';
  }

  switch (colorScheme) {
    case 'brand':
      return 'tw:text-text-link-brand';
    case 'success':
      return 'tw:text-text-success';
    case 'warning':
      return 'tw:text-text-warning';
    case 'error':
      return 'tw:text-text-error';
    case 'info':
      return 'tw:text-text-info';
    case 'default':
    default:
      return 'tw:text-text-secondary';
  }
};

export interface ChipProps
  extends Omit<React.ButtonHTMLAttributes<HTMLElement>, 'type'>,
    Omit<VariantProps<typeof chipVariants>, 'disabled'> {
  /**
   * Text content for the chip.
   * If children are provided, this prop is ignored.
   */
  text?: string;

  /**
   * Icon to display on the left side.
   *
   * @example
   * import TagIcon from 'src/assets/tag.svg';
   * <Chip text="Tag" leftIcon={TagIcon} />
   */
  leftIcon?: React.ComponentType<React.SVGProps<SVGSVGElement>>;

  /**
   * Icon to display on the right side.
   *
   * @example
   * import CloseIcon from 'src/assets/close.svg';
   * <Chip text="Removable" rightIcon={CloseIcon} />
   */
  rightIcon?: React.ComponentType<React.SVGProps<SVGSVGElement>>;

  /**
   * Props to pass to the left Icon component.
   *
   * @example
   * <Chip leftIcon={TagIcon} leftIconProps={{ colorClass: "text-icon-brand" }} />
   */
  leftIconProps?: Omit<IconProps, 'icon'>;

  /**
   * Props to pass to the right Icon component.
   *
   * @example
   * <Chip rightIcon={CloseIcon} rightIconProps={{ colorClass: "text-icon-secondary" }} />
   */
  rightIconProps?: Omit<IconProps, 'icon'>;

  /**
   * Click handler for the right icon.
   * Useful for close/remove functionality.
   *
   * @example
   * <Chip text="Removable" rightIcon={CloseIcon} onRightIconClick={() => handleRemove()} />
   */
  onRightIconClick?: (e: React.MouseEvent<HTMLSpanElement>) => void;

  /**
   * Click handler for the left icon.
   *
   * @example
   * <Chip text="Clickable" leftIcon={InfoIcon} onLeftIconClick={() => showInfo()} />
   */
  onLeftIconClick?: (e: React.MouseEvent<HTMLSpanElement>) => void;

  /**
   * Custom text color class to override the default color based on variant.
   * Use Tailwind text color classes.
   *
   * @example
   * <Chip text="Custom" textColorClass="text-text-error" />
   */
  textColorClass?: string;

  /**
   * Custom background color class to override the default background.
   * Use Tailwind background color classes.
   *
   * @example
   * <Chip text="Custom" bgColorClass="bg-error" />
   */
  bgColorClass?: string;

  interactive?: boolean;
}

const Chip = React.forwardRef<HTMLElement, ChipProps>(
  (
    {
      className,
      type,
      variant,
      colorScheme,
      size,
      text,
      leftIcon,
      rightIcon,
      leftIconProps,
      rightIconProps,
      onLeftIconClick,
      onRightIconClick,
      textColorClass,
      bgColorClass,
      interactive = false,
      disabled,
      children,
      ...props
    },
    ref
  ) => {
    const Comp = interactive ? 'button' : 'div';
    const getTypographyVariant = () => {
      if (type === 'badge' && size === 'large') {
        return 'h5';
      }

      return 'h6';
    };

    const getIconSizeClass = () => {
      switch (type) {
        case 'tag':
          return 'tw:w-m tw:h-m';
        case 'badge':
          return size === 'medium' ? 'tw:h-m tw:w-m' : 'tw:h-l tw:w-l';
        default:
          return 'tw:h-m tw:w-m';
      }
    };

    const getChipPaddingClass = () => {
      if (type === 'tag' && size === 'small') {
        return 'tw:px-xs tw:py-3xs';
      }
      return '';
    };

    // Determine text color: custom override > variant-based color
    const computedTextColorClass =
      textColorClass ||
      getTextColorClass(type, colorScheme, interactive, !!disabled);

    return (
      <Comp
        ref={ref as React.Ref<HTMLButtonElement & HTMLDivElement>}
        className={cn(
          interactive && 'tw:group',
          chipVariants({
            type,
            variant,
            colorScheme,
            size,
            interactive,
            disabled: !!disabled
          }),
          bgColorClass,
          getChipPaddingClass(),
          className
        )}
        {...(interactive
          ? { type: 'button' as const, disabled: !!disabled }
          : {})}
        {...props}
      >
        {children || (
          <>
            {leftIcon && (
              <span
                onClick={onLeftIconClick}
                className={cn(
                  'tw:leading-0',
                  onLeftIconClick ? 'tw:cursor-pointer' : undefined
                )}
              >
                <Icon
                  icon={leftIcon}
                  sizeClass={getIconSizeClass()}
                  colorClass={computedTextColorClass}
                  {...leftIconProps}
                />
              </span>
            )}
            {text && (
              <Typography
                variant={getTypographyVariant()}
                colorClass={computedTextColorClass}
                className=" tw:truncate"
              >
                {text}
              </Typography>
            )}
            {rightIcon && (
              <span
                onClick={onRightIconClick}
                className={cn(
                  'tw:leading-0',
                  onRightIconClick ? 'tw:cursor-pointer' : undefined
                )}
              >
                <Icon
                  icon={rightIcon}
                  sizeClass={getIconSizeClass()}
                  colorClass={computedTextColorClass}
                  {...rightIconProps}
                />
              </span>
            )}
          </>
        )}
      </Comp>
    );
  }
);

Chip.displayName = 'Chip';

export { Chip, chipVariants };
